if (!customElements.get('form-validate')) {
  class FormValidation extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) throw new Error('Form element does not exist');
    }

    toggleErrorMessage(input, field) {
      const errorMessageWrapper =
        field.querySelector('[data-message]');
      errorMessageWrapper.classList.toggle(
        'hidden',
        input.validity.valid
      );
      errorMessageWrapper.parentElement.classList.toggle(
        'has-error',
        !input.validity.valid
      );

      errorMessageWrapper.innerHTML =
        input.type === 'email'
          ? window.validationStrings.invalidEmail
          : input.validationMessage;
    }

    setFieldValidity(input) {
      input.setAttribute('aria-invalid', !input.validity.valid);
      
      // For checkboxes, try to find [data-input-wrapper] or fallback to parent structure
      let field = input.closest('[data-input-wrapper]');
      
      // If no [data-input-wrapper] found (e.g., old structure), try to find parent container
      if (!field && input.type === 'checkbox') {
        // Try to find a parent container that might have error message structure
        const parentContainer = input.closest('.form-row') || input.closest('.field') || input.closest('.checkbox');
        if (parentContainer) {
          // Check if there's a [data-message] element nearby
          const errorMessage = parentContainer.querySelector('[data-message]');
          if (errorMessage) {
            // Create a temporary wrapper structure for compatibility
            field = parentContainer;
            // Ensure the error message is accessible
            if (!field.hasAttribute('data-input-wrapper')) {
              // Use the existing error message element
              this.toggleCheckboxErrorMessage(input, parentContainer, errorMessage);
              return;
            }
          }
        }
      }
      
      if (!field) return;
      field.classList.toggle('has-error', !input.validity.valid);
      this.toggleErrorMessage(input, field);
    }

    toggleCheckboxErrorMessage(input, container, errorMessageWrapper) {
      const isValid = input.validity.valid;
      
      errorMessageWrapper.classList.toggle('hidden', isValid);
      container.classList.toggle('has-error', !isValid);
      
      if (!isValid) {
        errorMessageWrapper.textContent = input.validationMessage || 
                                         input.dataset.errorText || 
                                         'This field is required';
      } else {
        errorMessageWrapper.textContent = '';
      }
    }

    onSubmit(event) {
      if (!this.form.reportValidity()) {
        // Validate all required fields including checkboxes
        for (const el of this.form.elements) {
          if (el.hasAttribute && el.hasAttribute('required') && el.validity && !el.validity.valid) {
            this.setFieldValidity(el);
          }
        }
        event.preventDefault();
        return;
      }
      if (this.closest('register-form')) {
        if (!this.runRegisterValidators()) {
          event.preventDefault();
          return;
        }
      }
      event.preventDefault();
      this.form.submit();
    }

    setHandlers() {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      for (const input of this.form.elements) {
        if (input.hasAttribute('data-no-validate')) continue;

        ['input', 'blur', 'change', 'invalid'].forEach(eventName => {
          input.addEventListener(
            eventName,
            this.setInputHandler.bind(this)
          );
        });
      }
      
      // Setup universal checkbox validation for all required checkboxes
      this.setupCheckboxValidation();
      
      if (this.closest('register-form')) {
        this.setupRegisterValidation();
      }
    }

    setInputHandler(event) {
      if (event.type === 'invalid') {
        event.preventDefault();
      }

      this.setFieldValidity(event.target);
    }

    setupCheckboxValidation() {
      // Find all required checkboxes in the form
      const requiredCheckboxes = Array.from(this.form.elements).filter(
        el => el.type === 'checkbox' && el.hasAttribute('required')
      );

      requiredCheckboxes.forEach(checkbox => {
        // Get error message: try data-error-text attribute, then translation, then default
        let errorText = checkbox.dataset.errorText;
        if (!errorText) {
          // Try to find error message from nearby element (for backward compatibility)
          const errorElement = checkbox.closest('[data-input-wrapper]')?.querySelector('[data-message]') ||
                               checkbox.closest('.form-row')?.querySelector('[data-message]');
          if (errorElement && errorElement.dataset.errorText) {
            errorText = errorElement.dataset.errorText;
          }
        }
        // Fallback to translation or default
        if (!errorText) {
          errorText = window.translationStrings?.privacyRequired || 'This field is required';
        }
        
        // Set custom validity message
        checkbox.setCustomValidity(errorText);
        
        // Listen for changes to clear error when checked
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            checkbox.setCustomValidity('');
            this.setFieldValidity(checkbox);
          }
        });
        
        // Handle invalid event to show error using setFieldValidity
        checkbox.addEventListener('invalid', (event) => {
          event.preventDefault();
          this.setFieldValidity(checkbox);
        });
      });
    }

    setupRegisterValidation() {
      const dobInput = this.form.querySelector('#RegisterForm-dob');

      if (dobInput) {
        const validateDobRealTime = () => {
          // Get date value from Flatpickr if available
          let dateValue = dobInput.value;
          if (dobInput._flatpickr && dobInput._flatpickr.selectedDates.length > 0) {
            const date = dobInput._flatpickr.selectedDates[0];
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            dateValue = `${year}-${month}-${day}`;
          }
          
          if (dateValue) {
            if (this.getAge(dateValue) < 16) {
              this.showDobError();
            } else {
              this.hideDobError();
            }
          } else {
            this.hideDobError();
          }
        };
        dobInput.addEventListener('change', validateDobRealTime);
        dobInput.addEventListener('input', validateDobRealTime);
        dobInput.addEventListener('blur', () => {
          setTimeout(() => {
            let dateValue = dobInput.value;
            if (dobInput._flatpickr && dobInput._flatpickr.selectedDates.length > 0) {
              const date = dobInput._flatpickr.selectedDates[0];
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              dateValue = `${year}-${month}-${day}`;
            }
            if (dateValue && this.getAge(dateValue) < 16) {
              this.showDobError();
            }
          }, 0);
        });
      }
    }

    runRegisterValidators() {
      if (!this.validateDob()) return false;
      // Privacy checkbox validation is now handled universally by setupCheckboxValidation
      return true;
    }

    validateDob() {
      const dobInput = this.form.querySelector('#RegisterForm-dob');
      if (!dobInput) return true;
      
      // Get date value from Flatpickr if available, otherwise from input
      let dateValue = dobInput.value;
      if (dobInput._flatpickr && dobInput._flatpickr.selectedDates.length > 0) {
        const date = dobInput._flatpickr.selectedDates[0];
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateValue = `${year}-${month}-${day}`;
      }
      
      if (!dateValue) return true;
      if (this.getAge(dateValue) < 16) {
        this.showDobError();
        return false;
      }
      this.hideDobError();
      return true;
    }


    getAge(dateString) {
      if (!dateString) return 0;
      const today = new Date();
      // Handle both ISO format (YYYY-MM-DD) and Flatpickr format
      let birthDate;
      if (dateString.includes('-') && dateString.length === 10) {
        // ISO format
        birthDate = new Date(dateString);
      } else {
        // Try to parse Flatpickr format - get from Flatpickr instance if available
        const dobInput = this.form.querySelector('#RegisterForm-dob');
        if (dobInput && dobInput._flatpickr && dobInput._flatpickr.selectedDates.length > 0) {
          birthDate = dobInput._flatpickr.selectedDates[0];
        } else {
          birthDate = new Date(dateString);
        }
      }
      if (isNaN(birthDate.getTime())) return 0;
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    }

    showDobError() {
      const dobInput = this.form.querySelector('#RegisterForm-dob');
      if (!dobInput) return;
      const errorText = dobInput.dataset.dobErrorMessage || 'You must be at least 16 years old to register';
      const dobFieldWrapper = dobInput.closest('[data-input-wrapper]');
      let dobErrorMessage = dobFieldWrapper?.querySelector('[data-message]');

      if (dobFieldWrapper) dobFieldWrapper.classList.add('has-error');
      if (dobErrorMessage) {
        dobErrorMessage.textContent = errorText;
        dobErrorMessage.classList.remove('hidden');
      } else if (dobFieldWrapper) {
        dobErrorMessage = document.createElement('span');
        dobErrorMessage.className = 'field__message';
        dobErrorMessage.setAttribute('data-message', '');
        dobFieldWrapper.appendChild(dobErrorMessage);
        dobErrorMessage.textContent = errorText;
        dobErrorMessage.classList.remove('hidden');
      }
      dobInput.setAttribute('aria-invalid', 'true');
      dobInput.setCustomValidity(errorText);
    }

    hideDobError() {
      const dobInput = this.form.querySelector('#RegisterForm-dob');
      if (!dobInput) return;
      const dobFieldWrapper = dobInput.closest('[data-input-wrapper]');
      const dobErrorMessage = dobFieldWrapper?.querySelector('[data-message]');

      if (dobFieldWrapper) dobFieldWrapper.classList.remove('has-error');
      if (dobErrorMessage) {
        dobErrorMessage.classList.add('hidden');
        dobErrorMessage.textContent = '';
      }
      dobInput.setAttribute('aria-invalid', 'false');
      dobInput.setCustomValidity('');
    }


    connectedCallback() {
      this.setHandlers();
    }
  }

  customElements.define('form-validate', FormValidation);
}
